"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { Course } from "@/types";
import { chooseNextCourseLesson } from "@/lib/course-flow";
import type { UniversalAssessmentRequirement } from "@/lib/lesson-access";

type EnrollmentStatus = "enrolled" | "in_progress" | "completed" | null;

interface EnrollmentResponse {
  status: EnrollmentStatus;
  percentComplete?: number;
  completedLessons?: string[];
  error?: string;
}

interface CourseEnrollmentActionsProps {
  course: Course;
  progressMap?: Map<string, { completed: boolean; percentWatched: number }>;
  assessmentMap?: Map<string, Map<string, boolean>>;
  universalAssessmentResultsMap?: Map<string, unknown>;
  trackProgressMap?: Map<string, string[]>;
}

export function CourseEnrollmentActions({
  course,
  progressMap = new Map(),
  assessmentMap,
  universalAssessmentResultsMap,
  trackProgressMap,
}: CourseEnrollmentActionsProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<EnrollmentStatus>(null);
  const [error, setError] = useState<string | null>(null);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());

  const firstLessonSlug = useMemo(() => {
    const firstModule = course.modules[0];
    return firstModule?.lessons[0]?.slug ?? null;
  }, [course.modules]);

  const nextLessonSlug = useMemo(() => {
    const effectiveProgressMap = new Map(progressMap);
    completedLessons.forEach((lessonSlug) => {
      if (!effectiveProgressMap.has(lessonSlug)) {
        effectiveProgressMap.set(lessonSlug, { completed: true, percentWatched: 1 });
      }
    });

    const universalRequirementMap = new Map<string, UniversalAssessmentRequirement[]>();
    course.modules.forEach((module) => {
      const requirements = (module.universalAssessments ?? []).map((assessmentId) => ({
        assessmentId,
        completed: Boolean(universalAssessmentResultsMap?.has(assessmentId)),
      }));
      if (requirements.length > 0) {
        universalRequirementMap.set(module.id, requirements);
      }
    });

    return (
      chooseNextCourseLesson(
        course,
        effectiveProgressMap,
        assessmentMap,
        universalRequirementMap,
        trackProgressMap
      ) ?? firstLessonSlug
    );
  }, [
    assessmentMap,
    completedLessons,
    course,
    firstLessonSlug,
    progressMap,
    trackProgressMap,
    universalAssessmentResultsMap,
  ]);

  useEffect(() => {
    const loadEnrollment = async () => {
      try {
        const response = await fetch(`/api/learn/courses/${course.slug}/enrollment`, {
          credentials: "include",
        });

        if (response.status === 401) {
          setStatus(null);
          return;
        }

        if (!response.ok) {
          throw new Error("enrollment_fetch_failed");
        }

        const data = (await response.json()) as EnrollmentResponse;
        setStatus(data.status ?? null);

        // Set completed lessons from the response
        if (data.completedLessons && Array.isArray(data.completedLessons)) {
          setCompletedLessons(new Set(data.completedLessons));
        }
      } catch (err) {
        console.error(err);
        setError("We could not check your enrollment status. Try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadEnrollment().catch((err) => {
      console.error(err);
      setError("Unexpected error loading enrollment status.");
      setLoading(false);
    });
  }, [course.slug]);

  const handleEnroll = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/learn/courses/${course.slug}/enrollment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "enrolled" }),
        credentials: "include",
      });

      if (response.status === 401) {
        setError("Please sign in to enroll in this course.");
        setSubmitting(false);
        return;
      }

      if (!response.ok) {
        setError("We could not enroll you right now. Please try again.");
        setSubmitting(false);
        return;
      }

      setStatus("enrolled");
      if (firstLessonSlug) {
        router.push(`/courses/${course.slug}/lessons/${firstLessonSlug}`);
      }
    } catch (err) {
      console.error(err);
      setError("Unexpected error enrolling. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    const targetLesson = nextLessonSlug || firstLessonSlug;
    if (targetLesson) {
      router.push(`/courses/${course.slug}/lessons/${targetLesson}`);
    }
  };

  const buttonLabel = (() => {
    if (!status) {
      return "Start course";
    }
    if (status === "completed") {
      return "Review course";
    }
    return "Continue course";
  })();

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <Button
      size="lg"
      className="group w-full sm:w-auto px-8 py-6 text-base shadow-lg hover:shadow-xl"
      disabled={loading || submitting || (!firstLessonSlug && status !== null)}
      onClick={status ? handleContinue : handleEnroll}
    >
      {loading || submitting ? (
        "One moment..."
      ) : (
        <span className="flex items-center gap-2">
          {buttonLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      )}
    </Button>
  );
}
